import produce from "immer"
import { ASC, AVERAGE } from "const"
import { InferValueTypes } from "client/utils"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

export const SIL_MODEL_ACCOUNT_DEVICES = "#accountDevices"
export const SIL_MODEL_ACCOUNT_SUBSYSTEMS = "#accountSubsystems"
export const SIL_MODEL_PROJECT_DEVICES = "#projectDevices"
export const SIL_MODEL_PROJECT_SUBSYSTEMS = "#projectSubsystems"
export const SIL_MODEL_QID_PRODUCTS = "#qidProducts"

const initQidExpanded: types.SilModelDevicesQidExpanded = {
    productIds: [],
    modelIds: [],
    safetyFunctionIds: [],
    optionIds: [],
}

const initSilModelDevicesState: types.SilModelDevicesState = {
    activeTab: SIL_MODEL_ACCOUNT_DEVICES,
    modelTarget: null,
    onSelect: null,

    accountDevices: {
        deviceList: null,
        deviceIds: [],
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
    },

    accountSubsystems: {
        deviceList: null,
        deviceIds: [],
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
    },

    projectDevices: {
        deviceList: null,
        deviceIds: [],
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
    },

    projectSubsystems: {
        deviceList: null,
        deviceIds: [],
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },
    },

    qidProducts: {
        productList: null,
        productIds: [],
        sortColumn: "name",
        sortOrder: ASC,
        filters: {
            search: null,
        },

        expanded: initQidExpanded,

        datapointList: {},
        calculationMethod: AVERAGE,
    },
}

const silModelDevicesReducer = produce((
    draft: types.SilModelDevicesState = initSilModelDevicesState,
    action: ActionTypes = null,
): types.SilModelDevicesState => {
    switch (action.type) {
        case types.SIL_MODEL_DEVICES_ACTIVE_TAB:
            draft.activeTab = action.tab
            return draft

        case types.SIL_MODEL_DEVICES_MODEL_TARGET:
            draft.modelTarget = action.modelTarget
            return draft

        case types.SIL_MODEL_DEVICES_ON_SELECT:
            draft.onSelect = action.action
            return draft

        // account devices
        case types.SIL_MODEL_DEVICES_ACCOUNT_DEVICE_LIST:
            draft.accountDevices.deviceList = action.deviceList
            return draft

        case types.SIL_MODEL_DEVICES_ACCOUNT_DEVICE_IDS:
            draft.accountDevices.deviceIds = action.deviceIds
            return draft

        case types.SIL_MODEL_DEVICES_ACCOUNT_DEVICE_SORT:
            draft.accountDevices.sortColumn = action.sortColumn
            draft.accountDevices.sortOrder = action.sortOrder
            return draft

        case types.SIL_MODEL_DEVICES_ACCOUNT_DEVICE_FILTERS:
            draft.accountDevices.filters = action.filters
            return draft

        // account subsystems
        case types.SIL_MODEL_DEVICES_ACCOUNT_SUBSYSTEM_LIST:
            draft.accountSubsystems.deviceList = action.deviceList
            return draft

        case types.SIL_MODEL_DEVICES_ACCOUNT_SUBSYSTEM_IDS:
            draft.accountSubsystems.deviceIds = action.deviceIds
            return draft

        case types.SIL_MODEL_DEVICES_ACCOUNT_SUBSYSTEM_SORT:
            draft.accountSubsystems.sortColumn = action.sortColumn
            draft.accountSubsystems.sortOrder = action.sortOrder
            return draft

        case types.SIL_MODEL_DEVICES_ACCOUNT_SUBSYSTEM_FILTERS:
            draft.accountSubsystems.filters = action.filters
            return draft

        // project devices
        case types.SIL_MODEL_DEVICES_PROJECT_DEVICE_LIST:
            draft.projectDevices.deviceList = action.deviceList
            return draft

        case types.SIL_MODEL_DEVICES_PROJECT_DEVICE_IDS:
            draft.projectDevices.deviceIds = action.deviceIds
            return draft

        case types.SIL_MODEL_DEVICES_PROJECT_DEVICE_SORT:
            draft.projectDevices.sortColumn = action.sortColumn
            draft.projectDevices.sortOrder = action.sortOrder
            return draft

        case types.SIL_MODEL_DEVICES_PROJECT_DEVICE_FILTERS:
            draft.projectDevices.filters = action.filters
            return draft

        // project subsystems
        case types.SIL_MODEL_DEVICES_PROJECT_SUBSYSTEM_LIST:
            draft.projectSubsystems.deviceList = action.deviceList
            return draft

        case types.SIL_MODEL_DEVICES_PROJECT_SUBSYSTEM_IDS:
            draft.projectSubsystems.deviceIds = action.deviceIds
            return draft

        case types.SIL_MODEL_DEVICES_PROJECT_SUBSYSTEM_SORT:
            draft.projectSubsystems.sortColumn = action.sortColumn
            draft.projectSubsystems.sortOrder = action.sortOrder
            return draft

        case types.SIL_MODEL_DEVICES_PROJECT_SUBSYSTEM_FILTERS:
            draft.projectSubsystems.filters = action.filters
            return draft

        // qid products
        case types.SIL_MODEL_DEVICES_QID_PRODUCT_LIST:
            draft.qidProducts.productList = action.productList
            return draft

        case types.SIL_MODEL_DEVICES_QID_PRODUCT_IDS:
            draft.qidProducts.productIds = action.productIds
            return draft

        case types.SIL_MODEL_DEVICES_QID_PRODUCT_SORT:
            draft.qidProducts.sortColumn = action.sortColumn
            draft.qidProducts.sortOrder = action.sortOrder
            return draft

        case types.SIL_MODEL_DEVICES_QID_PRODUCT_FILTERS:
            draft.qidProducts.filters = action.filters
            return draft

        case types.SIL_MODEL_DEVICES_QID_EXPANDED:
            draft.qidProducts.expanded = {
                ...draft.qidProducts.expanded,
                ...action.expanded,
            }
            return draft

        case types.SIL_MODEL_DEVICES_QID_CLEAR_EXPANDED:
            draft.qidProducts.expanded = { ...initQidExpanded }
            return draft

        case types.SIL_MODEL_DEVICES_QID_DATAPOINT_LIST:
            draft.qidProducts.datapointList = {
                ...draft.qidProducts.datapointList,
                [action.productId]: action.datapoints,
            }
            return draft

        case types.SIL_MODEL_DEVICES_QID_CLEAR_DATAPOINT_LIST:
            draft.qidProducts.datapointList = {}
            return draft

        case types.SIL_MODEL_DEVICES_QID_CALCULATION_METHOD:
            draft.qidProducts.calculationMethod = action.calculationMethod
            return draft

        default:
            return draft
    }
})
export default silModelDevicesReducer
